import { baseApi } from './baseApi';

export interface SettingWithValue {
  id: string;
  key: string;
  name: string;
  description?: string;
  dataType: number; // 0=String, 1=Boolean, 2=Integer, 3=Decimal, 4=Json
  defaultValue?: string;
  category?: string;
  isSystem: boolean;
  isVisible: boolean;
  sortOrder: number;
  currentValue?: string;
  hasCustomValue: boolean;
  lastUpdated?: string;
}

export interface BoolSettingResponse {
  key: string;
  value: boolean;
}

export interface IntSettingResponse {
  key: string;
  value: number;
}

export interface SettingResponse {
  key: string;
  value: string;
}

export const settingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all settings
    getAllSettings: builder.query<SettingWithValue[], void>({
      query: () => '/settings',
      providesTags: ['AppSettings'],
    }),

    // Get settings by category
    getSettingsByCategory: builder.query<SettingWithValue[], string>({
      query: (category) => `/settings/category/${category}`,
      providesTags: ['AppSettings'],
    }),

    // Get a specific setting value
    getSetting: builder.query<SettingResponse, string>({
      query: (key) => `/settings/${key}`,
      providesTags: ['AppSettings'],
    }),

    // Get a boolean setting
    getBoolSetting: builder.query<BoolSettingResponse, string>({
      query: (key) => `/settings/${key}/bool`,
      providesTags: ['AppSettings'],
    }),

    // Get an integer setting
    getIntSetting: builder.query<IntSettingResponse, string>({
      query: (key) => `/settings/${key}/int`,
      providesTags: ['AppSettings'],
    }),

    // Update a setting
    updateSetting: builder.mutation<void, { key: string; value: string }>({
      query: ({ key, value }) => ({
        url: `/settings/${key}`,
        method: 'PUT',
        body: { value },
      }),
      invalidatesTags: ['AppSettings'],
    }),

    // Reset a setting to default (delete custom value)
    resetSetting: builder.mutation<void, string>({
      query: (key) => ({
        url: `/settings/${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AppSettings'],
    }),

    // Get all categories
    getSettingsCategories: builder.query<string[], void>({
      query: () => '/settings/categories',
      providesTags: ['AppSettings'],
    }),
  }),
});

export const {
  useGetAllSettingsQuery,
  useGetSettingsByCategoryQuery,
  useGetSettingQuery,
  useGetBoolSettingQuery,
  useGetIntSettingQuery,
  useUpdateSettingMutation,
  useResetSettingMutation,
  useGetSettingsCategoriesQuery,
} = settingsApi;
